import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AISystem,
  AISystemOverviewResponse,
  AISystemsResponse,
  AISystemSummaryDto
} from '../../types/AISystems.types';
import { logout } from './authSlice';

type AISystemsState = {
  systems: AISystem[];
  total: number;
  loading: boolean;
  error: string | null;
  currentSystem: AISystem | null;
  currentSystemLoading: boolean;
  currentSystemError: string | null;
  currentOverview: AISystemOverviewResponse | null;
  currentOverviewAiSystemId: string | null;
  currentOverviewLoading: boolean;
  currentOverviewError: string | null;
  filters: {
    search: string;
    entity?: string[];
    riskClassification?: string[];
    complianceStatus?: string[];
    owners?: string[];
    organization?: string[];
  };
  pagination: {
    limit: number;
    offset: number;
  };
  sorting: {
    sortBy?: string;
    order?: 'asc' | 'desc';
  };
  systemsSummary: AISystemSummaryDto | null;
  systemsSummaryLoading: boolean;
  systemsSummaryError: string | null;
  aiSystemStatus: 'draft' | 'created' | null;
  hasMajorSeverity: boolean;
  listScrollTop: number;
};

const getInitialState = (): AISystemsState => ({
  systems: [],
  total: 0,
  loading: false,
  error: null,
  currentSystem: null,
  currentSystemLoading: false,
  currentSystemError: null,
  currentOverview: null,
  currentOverviewAiSystemId: null,
  currentOverviewLoading: false,
  currentOverviewError: null,
  filters: {
    search: '',
    entity: [],
    riskClassification: [],
    complianceStatus: [],
    owners: [],
    organization: []
  },
  pagination: {
    limit: 10,
    offset: 0
  },
  sorting: {
    sortBy: undefined,
    order: undefined
  },
  systemsSummary: null,
  systemsSummaryLoading: false,
  systemsSummaryError: null,
  aiSystemStatus: null,
  hasMajorSeverity: false,
  listScrollTop: 0
});

const initialState = getInitialState();

export const aiSystemsSlice = createSlice({
  name: 'aiSystems',
  initialState,
  reducers: {
    prepareCurrentAISystem: (state, action: PayloadAction<string>) => {
      if (state.currentSystem?.id !== action.payload) {
        state.currentSystem = null;
      }
      state.currentSystemLoading = false;
      state.currentSystemError = null;
      state.currentOverview = null;
      state.currentOverviewAiSystemId = action.payload;
      state.currentOverviewLoading = false;
      state.currentOverviewError = null;
      state.hasMajorSeverity = false;
    },
    fetchSystemsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchSystemsSuccess: (state, action: PayloadAction<AISystemsResponse>) => {
      state.loading = false;
      state.systems = action.payload?.items || [];
      state.total = action.payload?.total || 0;
      state.error = null;
    },
    appendSystemsSuccess: (state, action: PayloadAction<AISystemsResponse>) => {
      const existingIds = new Set(state.systems.map((system) => system.id));
      const newItems = (action.payload?.items || []).filter(
        (system) => !existingIds.has(system.id)
      );

      state.loading = false;
      state.systems = [...state.systems, ...newItems];
      state.total = action.payload?.total || 0;
      state.error = null;
    },
    fetchSystemsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.pagination.offset = 0;
    },
    setEntityFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.entity = action.payload;
      state.pagination.offset = 0;
    },
    setRiskFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.riskClassification = action.payload;
      state.pagination.offset = 0;
    },
    setComplianceFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.complianceStatus = action.payload;
      state.pagination.offset = 0;
    },
    setOwnersFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.owners = action.payload;
      state.pagination.offset = 0;
    },
    setOrganizationFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.organization = action.payload;
      state.pagination.offset = 0;
    },
    resetFilters: (state) => {
      state.filters = {
        search: '',
        entity: [],
        riskClassification: [],
        complianceStatus: [],
        owners: [],
        organization: []
      };
      state.pagination.offset = 0;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload;
      state.pagination.offset = 0;
    },
    setOffset: (state, action: PayloadAction<number>) => {
      state.pagination.offset = action.payload;
    },
    setSorting: (
      state,
      action: PayloadAction<{ sortBy: string; order: 'asc' | 'desc' }>
    ) => {
      state.sorting.sortBy = action.payload.sortBy;
      state.sorting.order = action.payload.order;
      state.pagination.offset = 0;
    },
    clearError: (state) => {
      state.error = null;
    },
    fetchSystemsSummaryStart: (state) => {
      state.systemsSummaryLoading = true;
      state.systemsSummaryError = null;
    },
    fetchSystemsSummarySuccess: (
      state,
      action: PayloadAction<AISystemSummaryDto>
    ) => {
      state.systemsSummaryLoading = false;
      state.systemsSummary = action.payload;
      state.systemsSummaryError = null;
    },
    fetchSystemsSummaryFailure: (state, action: PayloadAction<string>) => {
      state.systemsSummaryLoading = false;
      state.systemsSummaryError = action.payload;
    },
    fetchCurrentSystemStart: (state) => {
      state.currentSystemLoading = true;
      state.currentSystemError = null;
    },
    fetchCurrentSystemSuccess: (state, action: PayloadAction<AISystem>) => {
      state.currentSystemLoading = false;
      state.currentSystem = action.payload;
      state.currentSystemError = null;
    },
    fetchCurrentSystemFailure: (state, action: PayloadAction<string>) => {
      state.currentSystemLoading = false;
      state.currentSystemError = action.payload;
    },
    fetchCurrentOverviewStart: (state) => {
      state.currentOverviewLoading = true;
      state.currentOverviewError = null;
    },
    fetchCurrentOverviewSuccess: (
      state,
      action: PayloadAction<AISystemOverviewResponse>
    ) => {
      state.currentOverviewLoading = false;
      state.currentOverview = action.payload;
      state.hasMajorSeverity = Boolean(action.payload.repositories?.hasMajor);
      state.currentOverviewError = null;
    },
    setCurrentOverviewAiSystemId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.currentOverviewAiSystemId = action.payload;
    },
    fetchCurrentOverviewFailure: (state, action: PayloadAction<string>) => {
      state.currentOverviewLoading = false;
      state.currentOverviewError = action.payload;
    },
    clearCurrentSystem: (state) => {
      state.currentSystem = null;
      state.currentSystemError = null;
      state.currentOverview = null;
      state.currentOverviewAiSystemId = null;
      state.currentOverviewError = null;
      state.hasMajorSeverity = false;
    },
    setAISystemStatus: (
      state,
      action: PayloadAction<'draft' | 'created' | null>
    ) => {
      state.aiSystemStatus = action.payload;
    },
    setHasMajorSeverity: (state, action: PayloadAction<boolean>) => {
      state.hasMajorSeverity = action.payload;
    },
    setListScrollTop: (state, action: PayloadAction<number>) => {
      state.listScrollTop = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => getInitialState());
  }
});

export const {
  prepareCurrentAISystem,
  fetchSystemsStart,
  fetchSystemsSuccess,
  appendSystemsSuccess,
  fetchSystemsFailure,
  setSearch,
  setEntityFilter,
  setRiskFilter,
  setComplianceFilter,
  setOwnersFilter,
  setOrganizationFilter,
  resetFilters,
  setLimit,
  setOffset,
  setSorting,
  clearError,
  fetchSystemsSummaryStart,
  fetchSystemsSummarySuccess,
  fetchSystemsSummaryFailure,
  fetchCurrentSystemStart,
  fetchCurrentSystemSuccess,
  fetchCurrentSystemFailure,
  fetchCurrentOverviewStart,
  fetchCurrentOverviewSuccess,
  fetchCurrentOverviewFailure,
  setCurrentOverviewAiSystemId,
  clearCurrentSystem,
  setAISystemStatus,
  setHasMajorSeverity,
  setListScrollTop
} = aiSystemsSlice.actions;

export default aiSystemsSlice.reducer;
